import { validateGeofence, getZones } from 'reducers/ZonesReducer';
import { asyncForEach } from '../../utils/functions';
import { pathToStr, offsetPoint } from 'utils/mapFunctions';

export const removeEmptyPaths = paths => {
  return paths.reduce((paths, path) => (getArea(path) > 0 ? [...paths, path] : paths), []);
};

export const getArea = polygon => {
  let total = 0;
  for (let i = 0; i < polygon.length; i++) {
    const addX = polygon[i][0];
    const addY = polygon[i === polygon.length - 1 ? 0 : i + 1][1];
    const subX = polygon[i === polygon.length - 1 ? 0 : i + 1][0];
    const subY = polygon[i][1];
    total += addX * addY * 0.5 - subX * subY * 0.5;
  }
  return Math.floor(Math.abs(total * 10000000));
};

export const removeLastPointFromPaths = paths => paths.forEach(p => p.pop());

export const getDistance = (p1, p2) => Math.hypot(p2[0] - p1[0], p2[1] - p1[1]);

export const arePolygonsValid = async paths => {
  let result = true;
  await asyncForEach(paths, async path => {
    const strPath = pathToStr(path);
    try {
      const isOk = await validateGeofence(strPath);
      if (!isOk) result = false;
    } catch (err) {
      return false;
    }
  });
  return result;
};

export const mergePolygons = async (polygon1, polygon2, pointIdx1, pointIdx2) => {
  const mergedPolygon = [];

  polygon1.forEach((point1, idx1) => {
    if (idx1 !== pointIdx1) {
      mergedPolygon.push(point1);
    } else {
      mergedPolygon.push(point1);
      for (let idx = pointIdx2; idx < polygon2.length; idx++) {
        mergedPolygon.push(polygon2[idx]);
      }
      for (let idx = 0; idx <= pointIdx2; idx++) {
        mergedPolygon.push(polygon2[idx]);
      }
      mergedPolygon.push(point1);
    }
  });

  // Apply microadjustments to connection points to make path valid
  let polyValid = false;
  for (let i = 0; i < 5; i++) {
    mergedPolygon[pointIdx1] = offsetPoint(polygon1[pointIdx1], i);
    for (let j = 1; j < 5; j++) {
      mergedPolygon[pointIdx1 + 1] = offsetPoint(polygon2[pointIdx2], j);
      polyValid = await arePolygonsValid([mergedPolygon]);
      if (polyValid) break;
    }
    if (polyValid) break;
  }
  return mergedPolygon;
};

export const findClosesPoints = (path1, paths) => {
  let point1Idx = 0; // index of point in path1
  let point2Idx = 0; // index of point in path2
  let path2Idx = 0; //  index of path2 in paths array
  let distance = getDistance(path1[0], paths[0][0]);
  path1.forEach((point1, idx1) => {
    paths.forEach((path2, pathIdx2) => {
      path2.forEach((point2, idx2) => {
        const dist = getDistance(point1, point2);
        if (dist < distance) {
          distance = dist;
          point1Idx = idx1;
          path2Idx = pathIdx2;
          point2Idx = idx2;
        }
      });
    });
  });
  return { point1Idx, point2Idx, path2Idx, distance };
};

export const strToPath2 = pathStr => {
  const path = pathStr.split(',').map(c => c.split(' '));
  path.pop();
  return path;
};

export const pathToStr2 = path => {
  const path2 = [...path];
  path2.push([...path[0]]);
  return path2.map(c => c.join(' ')).join(',');
};

export const joinPolygons = async polygons => {
  const allPaths = polygons.map(strToPath2);
  const paths = removeEmptyPaths(allPaths).map(p => removeDuplicatePoints(p));
  const validPaths = await arePolygonsValid(paths);
  removeLastPointFromPaths(paths);
  let joinedPath = paths.shift();
  while (paths.length) {
    const { point1Idx, point2Idx, path2Idx } = findClosesPoints(joinedPath, paths);
    joinedPath = await mergePolygons(joinedPath, paths[path2Idx], point1Idx, point2Idx);
    paths.splice(path2Idx, 1);
  }
  return validPaths ? pathToStr2(joinedPath) : false;
};

export const removeDuplicatePoints = path => {
  if (!path) return null;
  const newPath = [];
  path.forEach((point, idx) => {
    if (idx === 0) {
      newPath.push(point);
    } else {
      const prevPoint = path[idx - 1];
      const distance = getDistance(point, prevPoint);
      if (distance > 0.00000001) newPath.push(point);
    }
  });
  return newPath;
};

export const removeNameDuplicates = async zones => {
  const getNoStr = no => {
    const str = '00' + no;
    return '_' + str.substr(-2);
  };
  const getName = (name, no) => {
    const noStr = getNoStr(no);
    return name.substr(0, 17) + noStr;
  };
  const currentZones = (await getAllZones()).map(z => z.ZoneCode);
  zones.forEach(z => {
    const { ZoneCode } = z;
    let no = 1;
    if (currentZones.indexOf(ZoneCode) !== -1 || no > 99) {
      let newZoneCode = ZoneCode;
      while (currentZones.indexOf(newZoneCode) !== -1) {
        newZoneCode = getName(ZoneCode, no++);
      }
      z.ZoneCode = newZoneCode;
    }
    currentZones.push(z.ZoneCode);
  });
};

export const getAllZones = async () => {
  let zoneCodes = [];
  try {
    zoneCodes = await getZones();
  } catch (err) {
    return [];
  }
  return zoneCodes;
};

export const parsePolygon = data => {
  const polygon = data
    .trim()
    .split(' ')
    .map(c => {
      const arr = c.split(',').map(c => c.trim());
      return `${arr[0]} ${arr[1]}`;
    });
  const unique = [];
  polygon.forEach((el, idx) => {
    if (idx === 0 || el !== polygon[idx - 1]) unique.push(el);
  });
  const result = unique.join(',');
  return result;
};

export const parseMultiGeometry = async el => {
  const polygons = el.MultiGeometry[0].Polygon.map(p =>
    parsePolygon(p.outerBoundaryIs[0].LinearRing[0].coordinates[0])
  );
  return await joinPolygons(polygons);
};

export const checkMandatoryProps = (rows, mandatoryProps) => {
  const errors = [];
  const errMsg = prop => `${prop} property is missing`;
  rows.forEach(e => {
    const keys = Object.keys(e);
    mandatoryProps.forEach(key => {
      if (keys.indexOf(key) === -1 && errors.indexOf(errMsg(key)) === -1) errors.push(errMsg(key));
    });
  });
  return errors;
};

export const getPolygonFromXML = async el => {
  if (el.Polygon)
    return parsePolygon(el.Polygon[0].outerBoundaryIs[0].LinearRing[0].coordinates[0]);
  if (el.MultiGeometry) return await parseMultiGeometry(el);
  return [];
};

export const parseKlmInfo = data => {
  const info = {};
  data.forEach(i => {
    const name = i.$.name;
    const val = i._;
    info[name] = val;
  });
  return info;
};

export const parseXMLResult = async (result, tag) => {
  const data = [];
  let error = null;
  await asyncForEach(result.kml.Document[0].Folder[0].Placemark, async el => {
    const entry = parseKlmInfo(el.ExtendedData[0].SchemaData[0].SimpleData);
    entry.polygon = await getPolygonFromXML(el);
    if (entry.polygon) {
      data.push(entry);
    } else {
      error = `Polygons for ${entry[tag]} were not merged properly. Please check for possible intersections.`;
    }
  });
  return { data, error };
};

export default {
  removeEmptyPaths,
  getArea,
  removeLastPointFromPaths,
  getDistance,
  offsetPoint,
  arePolygonsValid,
  mergePolygons,
  findClosesPoints,
  strToPath2,
  pathToStr2,
  joinPolygons,
  removeDuplicatePoints,
  removeNameDuplicates,
  getAllZones,
  parsePolygon,
  parseMultiGeometry,
  checkMandatoryProps,
  getPolygonFromXML,
  parseXMLResult,
  parseKlmInfo,
};
